import { PlaylistModel, ArtistTrackModel } from '@/models'
import { Commit } from 'vuex'

const PLAYLISTS_STORAGE_KEY = 'robopolMusicPlaylists'

function readPlaylists() {
  if (typeof window === 'undefined') {
    return []
  }

  const raw = window.localStorage.getItem(PLAYLISTS_STORAGE_KEY)
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

function writePlaylists(playlists: PlaylistModel[]) {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(PLAYLISTS_STORAGE_KEY, JSON.stringify(playlists))
}

function createPlaylistPayload(name: string): PlaylistModel {
  const now = new Date()
  return {
    creationdate: now.toISOString(),
    id: `local-${now.getTime()}`,
    isLocal: true,
    name,
    shareurl: '',
    shorturl: '',
    tracks: [],
    'user_id': 'local-user',
    'user_name': 'You',
    zip: '',
  }
}

const state = {
  topPlaylists: readPlaylists()
}

interface InterfaceState {
  topPlaylists: PlaylistModel[];
}

const mutations = {
  setTopPlaylists(state: InterfaceState, payload: PlaylistModel[]) {
    state.topPlaylists = payload
  }
}

const getters = {
  getTopPlaylists(state: InterfaceState) {
    return state.topPlaylists
  },
  getPlaylistById: (state: InterfaceState) => (id: string) => {
    return state.topPlaylists.find((playlist) => playlist.id === id)
  }
}

const actions = {
  loadPlaylists({commit}: {commit: Commit}) {
    const playlists = readPlaylists()
    commit('setTopPlaylists', playlists)
    return Promise.resolve(playlists)
  },
  fetchTopPlaylists({dispatch}: {dispatch: Function}) {
    return dispatch('loadPlaylists')
  },
  fetchTopPlaylistsPaged({dispatch}: {dispatch: Function}) {
    return dispatch('loadPlaylists')
  },
  createPlaylist({state, commit}: {state: InterfaceState; commit: Commit}, payload: string) {
    const name = payload.trim()
    if (!name) {
      return Promise.resolve(null)
    }

    const playlist = createPlaylistPayload(name)
    const playlists = [playlist, ...state.topPlaylists]
    writePlaylists(playlists)
    commit('setTopPlaylists', playlists)
    return Promise.resolve(playlist)
  },
  deletePlaylist({state, commit}: {state: InterfaceState; commit: Commit}, payload: string) {
    const playlists = state.topPlaylists.filter((playlist) => playlist.id !== payload)
    writePlaylists(playlists)
    commit('setTopPlaylists', playlists)
    return Promise.resolve(playlists)
  },
  addTrackToPlaylist(
    {state, commit}: {state: InterfaceState; commit: Commit},
    payload: {playlistId: string; track: ArtistTrackModel}
  ) {
    const playlists = state.topPlaylists.map((playlist) => {
      if (playlist.id !== payload.playlistId) {
        return playlist
      }

      const playlistTracks = playlist.tracks ? [...playlist.tracks] : []
      const exists = playlistTracks.some((track) => track.id === payload.track.id)
      if (exists) {
        return playlist
      }

      return {
        ...playlist,
        tracks: [...playlistTracks, payload.track],
      }
    })

    writePlaylists(playlists)
    commit('setTopPlaylists', playlists)
    return Promise.resolve(playlists.find((playlist) => playlist.id === payload.playlistId) || null)
  },
  removeTrackFromPlaylist(
    {state, commit}: {state: InterfaceState; commit: Commit},
    payload: {playlistId: string; trackId: string}
  ) {
    const playlists = state.topPlaylists.map((playlist) => {
      if (playlist.id !== payload.playlistId) {
        return playlist
      }

      return {
        ...playlist,
        tracks: (playlist.tracks || []).filter((track) => track.id !== payload.trackId),
      }
    })

    writePlaylists(playlists)
    commit('setTopPlaylists', playlists)
    return Promise.resolve(playlists.find((playlist) => playlist.id === payload.playlistId) || null)
  }
}

const playlistModule = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

export default playlistModule
